import * as React from "react"
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group"
import { cn } from "../../lib/utils"

interface SegmentedOption {
  label?: React.ReactNode
  value: string | number
  icon?: React.ReactNode
  disabled?: boolean
}

interface SegmentedProps {
  options: SegmentedOption[]
  value?: string | number
  onChange?: (value: string | number) => void
  disabled?: boolean
  className?: string
  style?: React.CSSProperties
}

const Segmented = React.forwardRef<HTMLDivElement, SegmentedProps>(
  ({ className, options, value, onChange, disabled, style }, ref) => {
    const handleValueChange = (newValue: string) => {
      if (newValue) {
        // Try to preserve the original type (number or string)
        const option = options.find(opt => String(opt.value) === newValue)
        onChange?.(option?.value ?? newValue)
      }
    }

    return (
      <ToggleGroupPrimitive.Root
        ref={ref}
        type="single"
        value={String(value)}
        onValueChange={handleValueChange}
        disabled={disabled}
        className={cn(
          "inline-flex items-center rounded-md border border-input bg-muted p-0.5",
          className
        )}
        style={style}
      >
        {options.map((option) => (
          <ToggleGroupPrimitive.Item
            key={String(option.value)}
            value={String(option.value)}
            disabled={option.disabled}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-all",
              "rounded-sm",
              "text-muted-foreground hover:text-foreground",
              "data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
          >
            {option.icon}
            {option.label}
          </ToggleGroupPrimitive.Item>
        ))}
      </ToggleGroupPrimitive.Root>
    )
  }
)

Segmented.displayName = "Segmented"

export { Segmented, type SegmentedOption }
